import { Box, Grid } from '@mui/material'
import { Chat } from '@mui/icons-material'
import { FormikHelpers } from 'formik'
import _ from 'lodash'
import { inject } from 'mobx-react'
import * as React from 'react'
import * as Yup from 'yup'
import DatePickerField from '../common/DatePickerField/DatePickerField'
import ESDialog from '../common/ESDialog'
import FullWidthField from '../common/FullWidthField'
import { ICommunication, IPatient } from '../Definitions'
import { SwaggerResponse, TagDto } from '../generated_client'
import { IStores } from '../Stores'
import { getTagColor, getTagName } from '../Tags/TagFormValues'
import {
  createICommunicationFormValues,
  ICommunicationFormValues,
  isEditable,
  toICommunication,
} from './CommunicationFormValues'
import { Chip, TextField, Autocomplete } from '@mui/material'

const communication_types = ['Email', 'Phone', 'Text', 'Fax', 'Other']

const CommunicationSchema = Yup.object().shape({
  // ...existing code...
})

interface IEditCommunicationDialogProps {
  close?: () => void
  communication?: ICommunication
  getAllTags?: (filter: string, includeSystem: boolean) => void
  isLoading?: boolean
  isOpen?: boolean
  disabled?: boolean
  saveCommunication?: (
    communication: ICommunication
  ) => Promise<void> | Promise<SwaggerResponse<void>>
  selectedPatient?: IPatient
  tags?: TagDto[]
}

class EditCommunicationDialog extends React.Component<IEditCommunicationDialogProps> {
  public componentDidMount() {
    this.props.getAllTags!('', false)
  }

  public save = (
    values: ICommunicationFormValues,
    formikBag: FormikHelpers<ICommunicationFormValues>
  ) => {
    const communication = toICommunication(values, this.props.communication)
    this.props.saveCommunication!(communication).finally(() =>
      formikBag.setSubmitting(false)
    )
  }

  public render() {
    const { close, isLoading, isOpen, communication, tags, selectedPatient } = this.props

    const title =
      (communication!.isNew ? 'Add New Communication' : 'Edit Communication') +
      (selectedPatient ? ` for ${selectedPatient!.name}` : '')

    return (
      <ESDialog
        close={close!}
        initialValues={createICommunicationFormValues(communication)}
        isLoading={isLoading}
        open={isOpen!}
        save={this.save}
        title={title}
        titleIcon={<Chat />}
        validationSchema={CommunicationSchema}
      >
        {({ values, setFieldValue }) => (
          <Grid container spacing={3} style={{ marginBottom: '12px', marginTop: '12px' }}>
            <Grid container item xs={12} spacing={5}>
              <Grid item xs={12}>
                <Grid container direction="row" justifyContent="space-between">
                  <Grid item xs={3}>
                    <Box sx={{ '& .MuiInputBase-root': { height: 40 } }}>
                      <DatePickerField
                        name="communicationDate"
                        label="Date"
                        inputId="communicationDate"
                        fullWidth={true}
                        clearable
                        disabled={isEditable(communication)}
                        shrink={true}
                        variant="outlined"
                      />
                    </Box>
                  </Grid>
                  <Grid item xs={3}>
                    {!isEditable(communication) ? (
                      <Autocomplete
                        size="small"
                        id="type"
                        options={communication_types}
                        getOptionLabel={(option) => option}
                        value={values.type || null}
                        onChange={(_, value) => setFieldValue('type', value || '')}
                        disabled={isEditable(communication)}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label="Type"
                            variant="outlined"
                            required
                            InputLabelProps={{ shrink: true }}
                            sx={{
                              '& .MuiInputBase-root': {
                                height: 40,
                              },
                            }}
                          />
                        )}
                      />
                    ) : (
                      <FullWidthField
                        name="type"
                        label="Type"
                        variant="outlined"
                        disabled={isEditable(communication)}
                        sx={{
                          '& .MuiInputBase-root': {
                            height: 40,
                          },
                        }}
                      />
                    )}
                  </Grid>
                  <Grid item xs={3}>
                    <Box sx={{ '& .MuiInputBase-root': { height: 40 } }}>
                      <DatePickerField
                        name="dateOfService"
                        label="Date of Service"
                        inputId="dateOfService"
                        fullWidth={true}
                        clearable
                        disabled={isEditable(communication)}
                        shrink={true}
                        variant="outlined"
                      />
                    </Box>
                  </Grid>
                </Grid>
              </Grid>
              <Grid item xs={6}>
                <Autocomplete
                  size="small"
                  multiple
                  options={tags || []}
                  getOptionLabel={(option: TagDto) => this.getTagName(option)}
                  isOptionEqualToValue={(option: TagDto, value: TagDto) =>
                    option.id === value.id
                  }
                  value={(values.communicationTags || []).map((tag: any) =>
                    'init' in tag && 'toJSON' in tag ? tag : ({ ...tag } as TagDto)
                  )}
                  onChange={(_, newValue) => setFieldValue('communicationTags', newValue)}
                  filterSelectedOptions
                  disabled={isEditable(communication)}
                  renderTags={(
                    tagValue: TagDto[],
                    getTagProps: (arg0: { index: number }) => any
                  ) =>
                    tagValue.map((option, index) => (
                      <Chip
                        key={option.id ?? option.name ?? index}
                        label={this.getTagName(option)}
                        style={{
                          backgroundColor: this.getTagColor(option),
                          color: '#fff',
                        }}
                        {...getTagProps({ index })}
                      />
                    ))
                  }
                  renderOption={(props, option: TagDto) => (
                    <li {...props}>
                      <Chip
                        label={this.getTagName(option)}
                        style={{
                          backgroundColor: this.getTagColor(option),
                          color: '#fff',
                          marginRight: 8,
                          fontWeight: 500,
                        }}
                        size="small"
                      />
                    </li>
                  )}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      variant="outlined"
                      label="Tags"
                      fullWidth
                      InputLabelProps={{ shrink: true }}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12}>
                <FullWidthField
                  autoFocus
                  name="message"
                  label="Content"
                  rows="6"
                  disabled={isEditable(communication)}
                  multiline
                  variant="outlined"
                />
              </Grid>
            </Grid>
          </Grid>
        )}
      </ESDialog>
    )
  }

  private getTagName = (t: TagDto) => {
    return getTagName(t)
  }

  private getTagColor = (t: TagDto) => {
    return getTagColor(t)
  }

  public getValue(item: string) {
    return item
  }
}

const InjectedEditCommunicationDialog = inject<
  IStores,
  IEditCommunicationDialogProps,
  Partial<IEditCommunicationDialogProps>,
  any
>((stores: IStores) => ({
  close: stores.communications.closeEditDialog,
  communication: stores.communications.selectedCommunication,
  getAllTags: stores.tags.getAllTags,
  isLoading: stores.communications.isLoading,
  isOpen: stores.communications.isEditModalOpen,
  saveCommunication: stores.communications.saveCommunication,
  selectedPatient: stores.patients.selectedPatient,
  tags: stores.tags.tags,
}))(EditCommunicationDialog)

export default InjectedEditCommunicationDialog
